import * as React from "react"
import {
  type EntityParty,
  type IntervalOrigin,
  type SummaryFilterInput,
} from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgBuilding02 } from "@digits-shared/components/SVGIcons/line/Building02.svg"
import { IconSize } from "@digits-shared/components/UI/Icons/Icon"
import { Checkbox as FilterCheckbox } from "@digits-shared/DesignSystem/FormElements/Checkbox"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import colors from "@digits-shared/themes/colors"
import { BodyText } from "@digits-shared/themes/typography"
import styled from "styled-components"
import { useTextIsOverflowing } from "src/frontend/hooks/useTextIsOverflowing"
import {
  FilterAccordionContent,
  FilterAccordionHeader,
  FilterAccordionItem,
} from "src/shared/components/Filters/FilterAccordion"
import { usePartyFilters } from "src/shared/components/Filters/hooks/usePartyFilters"
import { FilterContentSection } from "src/shared/components/Filters/shared"
import { TransactionSearchBox } from "src/shared/components/Filters/TransactionSearchBox"
import { TransactionFilterType } from "src/shared/components/Filters/types"
import { PartyIcon } from "src/shared/components/PartyHover/PartyIcon"

const PartyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const Checkbox = styled(FilterCheckbox)``

const PartyRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;

  &:hover {
    ${Checkbox} {
      border-color: ${colors.accentBlue};
      background-color: ${colors.white};
    }
  }
`

const PartyInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const NoPartyIcon = styled.div`
  width: 25px;
  height: 25px;
  border-radius: 4px;
  border: 1px dashed ${colors.secondary60};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 10px;
    height: 10px;
    ${svgPathStyles(colors.secondary60, 1.5)};
  }
`

const PartyName = styled(BodyText)`
  position: relative;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 170px;
`

interface FilterByPartyProps {
  filters: Omit<SummaryFilterInput, "viewKey">
  selectedParties: Set<string>
  toggleParty: (party: EntityParty) => void
  includeMissingParty: true | undefined
  toggleIncludeMissingParty: () => void
  origin?: IntervalOrigin
  skip?: boolean
}

export const FilterByParty: React.FC<FilterByPartyProps> = ({
  filters,
  selectedParties,
  toggleParty,
  toggleIncludeMissingParty,
  includeMissingParty,
  origin,
  skip,
}) => {
  const [searchTerm, setSearchTerm] = React.useState<string>("")
  const { showNoPartyOption, parties } = usePartyFilters({
    filters,
    searchTerm,
    origin,
    includeMissingParty,
    skip,
  })

  const count = React.useMemo(() => {
    const includeMissingPartyCount = includeMissingParty && showNoPartyOption ? 1 : 0
    return selectedParties.size + includeMissingPartyCount
  }, [includeMissingParty, selectedParties, showNoPartyOption])

  return (
    <FilterAccordionItem value={TransactionFilterType.Party}>
      <FilterAccordionHeader Icon={SvgBuilding02} count={count}>
        Party
      </FilterAccordionHeader>
      <FilterAccordionContent>
        <FilterContentSection>
          <TransactionSearchBox
            placeholder="Lookup…"
            onChange={setSearchTerm}
            onSubmit={setSearchTerm}
            onClear={setSearchTerm}
            initialValue={searchTerm}
          />
        </FilterContentSection>
        <FilterContentSection $scrollable $maxHeight={200}>
          <PartyList>
            {showNoPartyOption && (
              <NoPartyRow checked={includeMissingParty} toggleNoParty={toggleIncludeMissingParty} />
            )}

            {parties.map((party) => (
              <Party
                key={party.id}
                party={party}
                checked={selectedParties.has(party.id)}
                toggleParty={toggleParty}
              />
            ))}
          </PartyList>
        </FilterContentSection>
      </FilterAccordionContent>
    </FilterAccordionItem>
  )
}

const Party: React.FC<{
  party: EntityParty
  checked: boolean
  toggleParty: (party: EntityParty) => void
}> = ({ party, checked, toggleParty }) => {
  const { isTextOverflowing, textElementRef } = useTextIsOverflowing<HTMLDivElement>()

  const toggleRow = React.useCallback(() => {
    toggleParty(party)
  }, [party, toggleParty])

  return (
    <PartyRow onClick={toggleRow}>
      <PartyInfo>
        <PartyIcon party={party} size={IconSize.SmallMedium} />
        <DigitsTooltip content={party.name} disabled={!isTextOverflowing}>
          <PartyName ref={textElementRef}>{party.name}</PartyName>
        </DigitsTooltip>
      </PartyInfo>

      <Checkbox checked={checked} onChange={toggleRow} />
    </PartyRow>
  )
}

const NoPartyRow: React.FC<{ checked: true | undefined; toggleNoParty: () => void }> = ({
  checked,
  toggleNoParty,
}) => (
  <PartyRow onClick={toggleNoParty}>
    <PartyInfo>
      <NoPartyIcon>
        <SvgBuilding02 />
      </NoPartyIcon>
      <PartyName>No Party</PartyName>
    </PartyInfo>

    <Checkbox checked={Boolean(checked)} onChange={toggleNoParty} />
  </PartyRow>
)
